/**
 * The URL vocabulary of the Integrations rail: which tabs exist, the parameter
 * that names one, how a raw query value narrows to a tab that exists, and the
 * link a screen anywhere else in the app writes when it means *go and sort out
 * the WhatsApp connection*.
 *
 * ## Why this is a module of its own, apart from the rail
 *
 * Slice 16 built `?tab=` inside `integration-tabs.tsx` — correctly, and with a
 * verify script either side of it — and then called it from **nowhere**. Every
 * cross-screen link that meant the WhatsApp connection still pointed at bare
 * `/integrations`, and `integrationTabFromParam(null)` resolves that to Email.
 * So a person who pressed "Connect one on Integrations" inside a WhatsApp panel
 * landed on the Amazon SES card with the WhatsApp form mounted, hidden and
 * `display: none` — strictly worse than the stacked page the tabs replaced,
 * where scrolling at least found it. A mechanism with no callers is not a
 * feature; it is a claim in a header.
 *
 * The repair is that every such caller writes its link through
 * {@link integrationTabPath}, and the callers are why this is not simply another
 * export on the rail. They are the Overview, the WhatsApp template editor, the
 * campaign composer and slice 15's checklist — and the last of those,
 * `onboarding-display.ts`, is a module whose only import is a type and which two
 * verify scripts load for their fixtures. Making it pull in a `<TabList>`
 * render, `@wend/ui`, lucide and `react-router-dom` in order to learn one string
 * is the wrong trade, and the trade would be made again by the next caller.
 *
 * So the vocabulary lives here with no React in it, and `integration-tabs.tsx`
 * is the rail that reads it. **The two names are deliberately not near-twins**
 * — this file is the *URL*, that one is the *strip* — because two modules whose
 * names differ by a letter is a trap this repository has already set for itself
 * once with `verify-whatsapp-status` and `verify-whatsapp-statuses`.
 *
 * ## Why the tab lives in a query parameter and not in the path
 *
 * Slice 15's first-run checklist links here, and a WhatsApp step that lands on
 * the Email tab is worse than the single scrolling page it replaced — so the
 * selection has to be addressable, survive a reload and paste into somebody
 * else's browser. Two mechanisms could do that; `?tab=` won on three counts.
 *
 *   - **An unknown value degrades instead of 404ing.** `/integrations/shopify`
 *     matches no route and lands on `NotFoundPage`; `?tab=shopify` renders the
 *     first tab and leaves the URL untouched, so the same link starts working
 *     the day that tab is built. The rail is the extension point for Flow's
 *     sources, which means links to tabs that do not exist yet are a *normal*
 *     event here rather than a mistake, and `App.tsx` already refuses to mint a
 *     route for software that has not been written.
 *   - **The route table describes resources, and this is one screen.** One
 *     heading, one nav item, one `AppLayout` title. A path segment would say
 *     there are three pages where there is one, and `AppLayout`'s Integrations
 *     entry would have to start matching by prefix for a reason that is not
 *     about navigation.
 *   - **It needs no change to `App.tsx`,** so the rail can grow a tab without
 *     touching the route table, which is the whole promise of the rail.
 *
 * The cost, stated: a query string is easier to lose than a path when a link is
 * retyped by hand, and `?tab=` is a weaker signal of hierarchy in a browser's
 * address bar. Both were judged smaller than sending a checklist link to a 404.
 *
 * **An unrecognised value is not rewritten out of the URL.** Correcting it
 * would destroy the link somebody shared, and the shared link is the thing this
 * mechanism exists to protect.
 *
 * ## Why an Email link carries `?tab=email` rather than nothing
 *
 * Email is the default, so `/integrations` already lands there and the parameter
 * looks redundant. It is not, for one reason that costs nothing to prevent and
 * is impossible to notice afterwards: {@link DEFAULT_INTEGRATION_TAB} is
 * `INTEGRATION_TAB_VALUES[0]`, derived rather than restated, and the whole point
 * of the rail is that Flow's sources get added to that list. An entry added at
 * the head of it silently redirects every bare link in the product to a channel
 * nobody meant. A link says where it means to go.
 */

/**
 * Every tab on the rail, in the order it is drawn. The list is the constant and
 * the type is derived from it, so a tab cannot exist in one and not the other.
 *
 * Adding one of Flow's sources later is three edits in three files: an entry
 * here, its word and its glyph in `integration-tabs.tsx`, and the panel itself
 * in the `panels` record `IntegrationsPage` hands to the rail — all three of
 * which the compiler demands together, because both records are total over this
 * union. Nothing else moves: no route, no nav entry, no second page.
 */
/*
 * `meta-ads` joins in slice 40. It is appended rather than inserted, and that
 * matters beyond tidiness: `DEFAULT_INTEGRATION_TAB` is `[0]`, so an entry at
 * the head would silently redirect every bare `/integrations` link in the
 * product to a channel nobody meant.
 *
 * Hyphen, not underscore: the enum member is `meta_ads` and this is a URL a
 * person reads, the same reason the WhatsApp tab is `whatsapp` and not
 * `meta_whatsapp`.
 */
export const INTEGRATION_TAB_VALUES = ['email', 'whatsapp', 'meta-ads'] as const;

export type IntegrationTabId = (typeof INTEGRATION_TAB_VALUES)[number];

/** The tab a URL with no usable `?tab=` shows. */
export const DEFAULT_INTEGRATION_TAB: IntegrationTabId = INTEGRATION_TAB_VALUES[0];

/** The query parameter that carries the selection. See the header. */
export const INTEGRATION_TAB_PARAM = 'tab';

/** Where the rail lives. One screen, one route — `App.tsx` has the other copy. */
export const INTEGRATIONS_PATH = '/integrations';

/** Narrows a raw query value. Anything else — absent, misspelt, a tab that does
 *  not exist yet — is not a tab id, and the caller falls back rather than 404s. */
export function isIntegrationTabId(value: string | null): value is IntegrationTabId {
  return value !== null && (INTEGRATION_TAB_VALUES as readonly string[]).includes(value);
}

/**
 * The tab selected by a URL. Total: every string, including `null`, maps to a
 * tab that exists, which is what keeps a stale or future link on this screen
 * instead of on the not-found page.
 */
export function integrationTabFromParam(value: string | null): IntegrationTabId {
  return isIntegrationTabId(value) ? value : DEFAULT_INTEGRATION_TAB;
}

/**
 * The link to one tab, for a screen somewhere else in the product.
 *
 * **Every cross-screen link into this page goes through here**, including the
 * ones that mean Email. Writing `'/integrations?tab=whatsapp'` by hand in five
 * files is how the parameter name and the tab ids drift apart from the rail that
 * reads them — and the drift is silent, because an unrecognised value renders
 * the first tab rather than failing.
 *
 * It builds the string rather than taking `URLSearchParams`, because a tab id is
 * one of two literals and neither needs escaping; a caller wanting to carry
 * other parameters is a caller the rail itself already serves, from inside the
 * page, where `setSearchParams` merges them.
 */
export function integrationTabPath(tab: IntegrationTabId): string {
  return `${INTEGRATIONS_PATH}?${INTEGRATION_TAB_PARAM}=${tab}`;
}
